<template>
  <div class="content-panel step-four-panel" style="max-width: 1000px;">
    <div class="panel-header">
      <div class="panel-icon">
        <v-icon size="32" color="#1e3a8a">mdi-account-tie</v-icon>
      </div>
      <div class="panel-title-section">
        <h2 class="panel-title">사회복지사/센터장 급여 설정하기</h2>
      </div>
      <div class="panel-header-actions">
        <template v-if="isConfirmed">
          <button class="btn-header-action" @click="handleRelease" :disabled="isConfirming">
            <v-progress-circular
              v-if="isConfirming"
              indeterminate
              size="16"
              width="2"
              color="#f59e0b"
              class="mr-1"
            />
            <v-icon v-else size="18">mdi-lock-open-variant</v-icon>
            <span>확정 해제</span>
          </button>
        </template>
        <template v-else>
          <button 
            class="btn-header-action primary confirm-emphasis" 
            @click="handleConfirm" 
            :disabled="!stepCompleted || isConfirming"
          >
            <v-progress-circular
              v-if="isConfirming"
              indeterminate
              size="16"
              width="2"
              color="#fff"
              class="mr-1"
            />
            <v-icon v-else size="18">mdi-lock-check</v-icon>
            <span>급여 설정 확정하기</span>
          </button>
        </template>
      </div>
    </div>

    <div class="panel-body">
      <!-- 확정 상태 알림 배너 -->
      <div v-if="isConfirmed" class="confirmed-notice-banner">
        <v-icon size="20" color="#f59e0b">mdi-lock</v-icon>
        <span class="notice-text">
          급여 설정이 <strong>확정</strong>되어 수정할 수 없습니다.
          수정하려면 상단의 <strong>확정 해제</strong> 버튼을 눌러주세요.
        </span>
        <button class="btn-release-inline" @click="handleRelease" :disabled="isConfirming">
          <v-icon size="16">mdi-lock-open-variant</v-icon>
          확정 해제
        </button>
      </div>

      <!-- TabSabokWage 컴포넌트 임베드 -->
      <TabSabokWage :is-magam="isConfirmed" ref="tabSabokWageRef" />
    </div>

    <!-- 확정 확인 다이얼로그 -->
    <v-dialog v-model="showConfirmDialog" max-width="440px">
      <v-card class="confirm-dialog">
        <div class="confirm-dialog-header">
          <v-icon size="48" color="#10b981">mdi-lock-check</v-icon>
          <h3>급여 설정 확정</h3>
        </div>
        <div class="confirm-dialog-body">
          <p>사회복지사/센터장 급여 설정을 확정하시겠습니까?</p>
          <p>확정 후에는 급여 설정을 수정할 수 없습니다.</p>
        </div>
        <div class="confirm-dialog-footer">
          <button class="btn-cancel" @click="showConfirmDialog = false">취소</button>
          <button class="btn-confirm" @click="proceedConfirm">확정</button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 해제 확인 다이얼로그 -->
    <v-dialog v-model="showReleaseDialog" max-width="440px">
      <v-card class="confirm-dialog">
        <div class="confirm-dialog-header warning">
          <v-icon size="48" color="#f59e0b">mdi-alert-circle</v-icon>
          <h3>확정 해제</h3>
        </div>
        <div class="confirm-dialog-body">
          <p>급여 설정 확정을 해제하시겠습니까?</p>
          <p>해제 후에는 다시 수정이 가능합니다.</p>
        </div>
        <div class="confirm-dialog-footer">
          <button class="btn-cancel" @click="showReleaseDialog = false">취소</button>
          <button class="btn-confirm warning" @click="proceedRelease">해제</button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import TabSabokWage from '@/components/wage/TabSabokWage.vue';

const props = defineProps<{
  stepCompleted: boolean;
  isConfirmed?: boolean;
  isConfirming?: boolean;
}>();

const emit = defineEmits<{
  (e: 'confirm'): void;
  (e: 'release'): void;
}>();

const store = useStore();

const isConfirmed = computed(() => !!props.isConfirmed);
const isConfirming = computed(() => !!props.isConfirming);

// TabSabokWage 참조
const tabSabokWageRef = ref<any>(null);

// 확정/해제 확인 다이얼로그 상태
const showConfirmDialog = ref(false);
const showReleaseDialog = ref(false);

// 반응성 트리거를 위한 카운터
const dataUpdateTrigger = ref(0);

// 확정 가능 여부를 체크하는 함수
const checkStepCompleted = () => {
  // props로 전달된 값이 있으면 우선 사용 (DB 확정 상태)
  if (props.stepCompleted) return true;
  
  // TabSabokWage의 데이터가 있는지 확인
  if (!tabSabokWageRef.value) {
    return false;
  }
  
  const staffData = tabSabokWageRef.value.staffData;
  const staffSettings = tabSabokWageRef.value.staffSettings || [];
  
  // 데이터가 없으면 false
  if (!staffData || staffData.length === 0) {
    return false;
  }
  
  // 저장된 직원이 1명 이상 있는지 확인 (empCode가 있는 직원)
  let validStaffCount = 0;
  
  for (let i = 0; i < staffData.length; i++) {
    const staff = staffData[i];
    
    // empCode가 없으면 아직 저장되지 않은 임시 데이터이므로 스킵
    if (!staff.empCode) {
      continue;
    }
    
    const settings = staffSettings[i];
    const deriveNumericValue = (value: any) => {
      if (typeof value === 'number') return value;
      if (value === null || value === undefined) return 0;
      return parseFloat(String(value).replace(/,/g, '')) || 0;
    };
    
    const timePaySource = settings?.timePay ?? staff.timePay;
    const workHoursSource = settings?.workHours ?? staff.workHours;
    
    // 시급과 근무시간 확인 (숫자로 변환하여 확인)
    const timePay = deriveNumericValue(timePaySource);
    const workHours = deriveNumericValue(workHoursSource);
    
    
    // 시급과 근무시간이 모두 0보다 크면 유효한 직원으로 카운트
    if (timePay > 0 && workHours > 0) {
      validStaffCount++;
    }
  }
  
  const isCompleted = validStaffCount > 0;
  
  return isCompleted;
};

// 확정 가능 여부 계산 (computed)
const stepCompleted = computed(() => {
  // 반응성 트리거 사용
  dataUpdateTrigger.value;
  return checkStepCompleted();
});

// 주기적으로 체크 (1초마다)
let checkInterval: any = null;

// 컴포넌트 마운트 시 주기적 체크 시작
watch(tabSabokWageRef, (newVal) => {
  if (newVal && !checkInterval) {
    // 초기 체크
    nextTick(() => {
      dataUpdateTrigger.value++;
    });
    
    // 5초 동안만 주기적으로 체크 (데이터 로드 대기)
    let count = 0;
    checkInterval = setInterval(() => {
      dataUpdateTrigger.value++;
      count++;
      
      // 10번 체크 후 중지 (10초)
      if (count >= 10) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }, 1000);
  }
}, { immediate: true });

// 확정 버튼 클릭
const handleConfirm = () => {
  showConfirmDialog.value = true;
};

const proceedConfirm = async () => {
  showConfirmDialog.value = false;
  
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    store.commit('showSnackbar', { text: '사용자 정보를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  if (!tabSabokWageRef.value) {
    store.commit('showSnackbar', { text: '직원 데이터를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  try {
    const searchParams = tabSabokWageRef.value.searchParams;
    const sisulCode = user.m_sisul_code;
    const lebYm = searchParams.lebYm;
    
    // hh_emp_sabok_work_info 테이블에서 저장된 임금대장 데이터 조회
    const workInfoResponse = await axios.get(`/api/calc/work-info/${sisulCode}/${lebYm}`, {
      withCredentials: true
    });
    
    if (!workInfoResponse.data.success || !workInfoResponse.data.data) {
      store.commit('showSnackbar', { 
        text: '저장된 급여 데이터가 없습니다. 먼저 급여 설정을 저장해주세요.', 
        color: 'warning' 
      });
      return;
    }
    
    const workInfoData = workInfoResponse.data.data;
    
    // 직종별 데이터 집계
    let yoboTotalCount = 0;
    let yoboTotalWageSum = 0;
    let yoboTotalEmployeeDeduction = 0;
    let yoboTotalCenterDeduction = 0;
    let yoboTotalNonTaxable = 0;
    let yoboTotalAge = 0;
    let yoboTotalAgeCount = 0;
    let yoboTotalSugaSum = 0;
    let yoboTotalCenterProfit = 0;
    let yoboTotalEmpSavings = 0;
    let yoboTotalCenterSavings = 0;
    
    let sabokTotalCount = 0;
    let sabokTotalWageSum = 0;
    let sabokTotalEmployeeDeduction = 0;
    let sabokTotalCenterDeduction = 0;
    let sabokTotalNonTaxable = 0;
    let sabokTotalAge = 0;
    let sabokTotalAgeCount = 0;
    let sabokTotalCenterProfit = 0;
    let sabokTotalEmpSavings = 0;
    let sabokTotalCenterSavings = 0;
    
    // 수급자 수 계산을 위해 요양보호사 데이터 조회
    const uniquePatients = new Set<string>();
    try {
      const yoboResponse = await axios.get('/api/calc/caregiver-member-join', {
        params: { sisulCode, lebYm },
        withCredentials: true
      });
      if (yoboResponse.data.success && yoboResponse.data.data) {
        yoboResponse.data.data.forEach((item: any) => {
          if (item.leb_patient_code) {
            uniquePatients.add(item.leb_patient_code);
          }
        });
      }
    } catch (e) {
      console.warn('수급자 수 조회 오류:', e);
    }
    
    // 저장된 데이터를 직종별로 분류하여 합산
    workInfoData.forEach((item: any) => {
      const empJob = item.emp_job || '';
      const isYobo = empJob === '요양보호사';
      
      // 나이 계산 (생년월일 기준)
      let age = 0;
      if (item.emp_birth) {
        const birthDate = new Date(item.emp_birth);
        const baseYear = parseInt(lebYm.substring(0, 4));
        const baseMonth = parseInt(lebYm.substring(4, 6));
        const baseDate = new Date(baseYear, baseMonth, 0);
        age = baseDate.getFullYear() - birthDate.getFullYear();
        if (baseDate.getMonth() < birthDate.getMonth() ||
          (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())) {
          age--;
        }
      }
      
      if (isYobo) {
        // 요양보호사
        yoboTotalCount++;
        yoboTotalWageSum += Number(item.ewi_total_pay) || 0;
        yoboTotalEmployeeDeduction += Number(item.ewi_emp_total_deduction) || 0;
        yoboTotalCenterDeduction += Number(item.ewi_center_total_deduction) || 0;
        yoboTotalNonTaxable += Number(item.ewi_non_taxable_amount) || 0;
        yoboTotalSugaSum += Number(item.ewi_monthly_pay) || 0;
        yoboTotalCenterProfit += Number(item.ewi_center_profit) || 0;
        yoboTotalEmpSavings += Number(item.ewi_emp_tax_savings) || 0;
        yoboTotalCenterSavings += Number(item.ewi_center_tax_savings) || 0;
        if (age > 0) {
          yoboTotalAge += age;
          yoboTotalAgeCount++;
        }
      } else {
        // 사회복지사/센터장/사무 등
        sabokTotalCount++;
        sabokTotalWageSum += Number(item.ewi_total_pay) || 0;
        sabokTotalEmployeeDeduction += Number(item.ewi_emp_total_deduction) || 0;
        sabokTotalCenterDeduction += Number(item.ewi_center_total_deduction) || 0;
        sabokTotalNonTaxable += Number(item.ewi_non_taxable_amount) || 0;
        sabokTotalCenterProfit += Number(item.ewi_center_profit) || 0;
        sabokTotalEmpSavings += Number(item.ewi_emp_tax_savings) || 0;
        sabokTotalCenterSavings += Number(item.ewi_center_tax_savings) || 0;
        if (age > 0) {
          sabokTotalAge += age;
          sabokTotalAgeCount++;
        }
      }
    });
    
    const yoboAverageAge = yoboTotalAgeCount > 0 ? Math.round(yoboTotalAge / yoboTotalAgeCount) : 0;
    const sabokAverageAge = sabokTotalAgeCount > 0 ? Math.round(sabokTotalAge / sabokTotalAgeCount) : 0;
    const totalAgeCount = yoboTotalAgeCount + sabokTotalAgeCount;
    const totalAverageAge = totalAgeCount > 0 
      ? Math.round((yoboTotalAge + sabokTotalAge) / totalAgeCount) 
      : 0;

    // 마감 데이터 준비
    const magamData = [
      // 요양보호사 데이터
      {
        wm_sisul_code: sisulCode,
        wm_ym: lebYm,
        wm_job_type: '요양보호사',
        wm_count: yoboTotalCount,
        wm_wage_sum: yoboTotalWageSum,
        wm_emp_deduction: yoboTotalEmployeeDeduction,
        wm_center_deduction: yoboTotalCenterDeduction,
        wm_non_taxable: yoboTotalNonTaxable,
        wm_emp_savings: yoboTotalEmpSavings,
        wm_center_savings: yoboTotalCenterSavings,
        wm_center_profit: yoboTotalCenterProfit,
        wm_avg_age: yoboAverageAge,
        wm_suga_sum: yoboTotalSugaSum,
        wm_pacient_count: uniquePatients.size
      },
      // 사회복지사/센터장 데이터
      {
        wm_sisul_code: sisulCode,
        wm_ym: lebYm,
        wm_job_type: '사회복지사/센터장',
        wm_count: sabokTotalCount,
        wm_wage_sum: sabokTotalWageSum,
        wm_emp_deduction: sabokTotalEmployeeDeduction,
        wm_center_deduction: sabokTotalCenterDeduction,
        wm_non_taxable: sabokTotalNonTaxable,
        wm_emp_savings: sabokTotalEmpSavings,
        wm_center_savings: sabokTotalCenterSavings,
        wm_center_profit: sabokTotalCenterProfit,
        wm_avg_age: sabokAverageAge,
        wm_suga_sum: 0,
        wm_pacient_count: 0
      },
      // 전체 합계
      {
        wm_sisul_code: sisulCode,
        wm_ym: lebYm,
        wm_job_type: '전체합계',
        wm_count: yoboTotalCount + sabokTotalCount,
        wm_wage_sum: yoboTotalWageSum + sabokTotalWageSum,
        wm_emp_deduction: yoboTotalEmployeeDeduction + sabokTotalEmployeeDeduction,
        wm_center_deduction: yoboTotalCenterDeduction + sabokTotalCenterDeduction,
        wm_non_taxable: yoboTotalNonTaxable + sabokTotalNonTaxable,
        wm_emp_savings: yoboTotalEmpSavings + sabokTotalEmpSavings,
        wm_center_savings: yoboTotalCenterSavings + sabokTotalCenterSavings,
        wm_center_profit: yoboTotalCenterProfit + sabokTotalCenterProfit,
        wm_avg_age: totalAverageAge,
        wm_suga_sum: yoboTotalSugaSum,
        wm_pacient_count: uniquePatients.size
      }
    ];

    // 마감 데이터 저장 API 호출
    const response = await axios.post('/api/calc/wage-magam', {
      sisulCode: sisulCode,
      lebYm: lebYm,
      magamData: magamData
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { 
        text: '✅ 급여 설정이 확정되고 마감 처리되었습니다.', 
        color: 'success' 
      });
      emit('confirm');
    } else {
      store.commit('showSnackbar', { 
        text: '마감 처리에 실패했습니다: ' + (response.data.message || '알 수 없는 오류'), 
        color: 'error' 
      });
    }
  } catch (error: any) {
    console.error('마감 처리 오류:', error);
    store.commit('showSnackbar', { 
      text: '마감 처리 중 오류가 발생했습니다: ' + (error.response?.data?.message || error.message), 
      color: 'error' 
    });
  }
};

// 해제 버튼 클릭
const handleRelease = () => {
  showReleaseDialog.value = true;
};

const proceedRelease = async () => {
  showReleaseDialog.value = false;
  
  const user = store.state.user;
  if (!user || !user.m_sisul_code) {
    store.commit('showSnackbar', { text: '사용자 정보를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  if (!tabSabokWageRef.value) {
    store.commit('showSnackbar', { text: '직원 데이터를 확인할 수 없습니다.', color: 'error' });
    return;
  }

  try {
    const searchParams = tabSabokWageRef.value.searchParams;
    
    // 마감 해제 API 호출
    const response = await axios.put('/api/calc/wage-magam/release', {
      sisulCode: user.m_sisul_code,
      lebYm: searchParams.lebYm
    }, { withCredentials: true });

    if (response.data.success) {
      store.commit('showSnackbar', { 
        text: '✅ 급여 설정 확정이 해제되었습니다.', 
        color: 'success' 
      });
      emit('release');
    } else {
      store.commit('showSnackbar', { 
        text: '확정 해제에 실패했습니다.', 
        color: 'error' 
      });
    }
  } catch (error: any) {
    console.error('확정 해제 오류:', error);
    store.commit('showSnackbar', { 
      text: '확정 해제 중 오류가 발생했습니다: ' + (error.response?.data?.message || error.message), 
      color: 'error' 
    });
  }
};

onMounted(async () => {
  // 초기 렌더 직후 데이터 기준으로 버튼 활성화 여부 재계산
  await nextTick();
  dataUpdateTrigger.value++;
});
</script>

<style lang="scss">
@import '@/assets/styles/wageProcess.scss';
@import '@/assets/styles/StepThree.scss';

.step-four-panel {
  .panel-icon {
    i {
      color: #8b5cf6 !important;
    }
  }
  
  .panel-title {
    color: #1a1a1a;
  }
}
</style>
